// Festival calendar: decides which festival (if any) is active on a given day,
// plus the small shared state used by the login greeting and the ambient theme.
//
// Fixed-date festivals are computed from the year, so they never need updating.
// Lunar festivals move every year, so their dates come from LUNAR_DATES below.
// Those were calculated astronomically for New Delhi using each festival's
// traditional rule (tithi at sunrise / midday / evening / midnight) and checked
// against published Drik Panchang dates. Extend the table before 2036; until then
// nothing needs touching. A missing year simply means "no festival that year".

// Main festival day for each year, as 'MM-DD'.
export const LUNAR_DATES = {
  // Lakshmi Puja day
  diwali: {
    2025: '10-20', 2026: '11-08', 2027: '10-29', 2028: '10-17', 2029: '11-05', 2030: '10-26',
    2031: '11-14', 2032: '11-02', 2033: '10-22', 2034: '11-10', 2035: '10-30',
  },
  // Rangwali Holi / Dhulandi (colour day); Holika Dahan is the evening before
  holi: {
    2025: '03-14', 2026: '03-04', 2027: '03-22', 2028: '03-11', 2029: '03-01', 2030: '03-20',
    2031: '03-09', 2032: '03-27', 2033: '03-16', 2034: '03-05', 2035: '03-24',
  },
  'maha-shivaratri': {
    2025: '02-26', 2026: '02-15', 2027: '03-06', 2028: '02-23', 2029: '02-11', 2030: '03-02',
    2031: '02-20', 2032: '03-10', 2033: '02-27', 2034: '02-17', 2035: '03-08',
  },
  'raksha-bandhan': {
    2025: '08-09', 2026: '08-28', 2027: '08-17', 2028: '08-05', 2029: '08-24', 2030: '08-13',
    2031: '08-03', 2032: '08-21', 2033: '08-10', 2034: '08-29', 2035: '08-18',
  },
  // Night of Ashtami; some calendars mark the next day, so the window covers both
  janmashtami: {
    2025: '08-15', 2026: '09-04', 2027: '08-24', 2028: '08-13', 2029: '08-31', 2030: '08-20',
    2031: '08-09', 2032: '08-27', 2033: '08-17', 2034: '09-05', 2035: '08-26',
  },
  'ganesh-chaturthi': {
    2025: '08-27', 2026: '09-14', 2027: '09-04', 2028: '08-23', 2029: '09-11', 2030: '09-01',
    2031: '09-20', 2032: '09-08', 2033: '08-28', 2034: '09-16', 2035: '09-05',
  },
  // First day of Sharad Navratri (Ghatasthapana); runs until the day before Dussehra
  navratri: {
    2025: '09-22', 2026: '10-11', 2027: '10-01', 2028: '09-19', 2029: '10-08', 2030: '09-28',
    2031: '10-17', 2032: '10-05', 2033: '09-24', 2034: '10-13', 2035: '10-02',
  },
  dussehra: {
    2025: '10-02', 2026: '10-20', 2027: '10-09', 2028: '09-27', 2029: '10-16', 2030: '10-06',
    2031: '10-25', 2032: '10-14', 2033: '10-03', 2034: '10-22', 2035: '10-11',
  },
  // Kartik Purnima (Gurpurab)
  'guru-nanak-jayanti': {
    2025: '11-05', 2026: '11-24', 2027: '11-14', 2028: '11-02', 2029: '11-21', 2030: '11-10',
    2031: '11-28', 2032: '11-17', 2033: '11-06', 2034: '11-26', 2035: '11-15',
  },
};

const fixed = (month, day) => (year) => new Date(year, month - 1, day);
const lunar = (id) => (year) => lunarDate(id, year);

// Each festival:
//   anchor(year)  main day          before/after  days of the window around it (inclusive)
//   end(year)     optional explicit last day (overrides `after`)
//   effect        full-screen login animation    ambient  subtle theme while working
// Order matters only when two windows overlap on the same day: the first match wins.
export const FESTIVALS = [
  {
    id: 'republic-day', anchor: fixed(1, 26), before: 0, after: 0,
    effect: 'tricolor', ambient: 'tricolor',
    title: 'Happy Republic Day!', emoji: '🇮🇳', accent: '#f97316',
    message: 'Celebrating the spirit of our Constitution. Jai Hind!',
  },
  {
    id: 'independence-day', anchor: fixed(8, 15), before: 0, after: 0,
    effect: 'tricolor', ambient: 'tricolor',
    title: 'Happy Independence Day!', emoji: '🇮🇳', accent: '#f97316',
    message: 'Saluting the freedom we cherish and the nation we build together. Jai Hind!',
  },
  {
    id: 'makar-sankranti', anchor: fixed(1, 14), before: 0, after: 1, // Jan 14–15
    effect: 'kites', ambient: 'kites',
    title: 'Happy Makar Sankranti!', emoji: '🪁', accent: '#0ea5e9',
    message: 'May your dreams fly as high as the kites this season.',
  },
  {
    id: 'maha-shivaratri', anchor: lunar('maha-shivaratri'), before: 0, after: 0,
    effect: 'lights', palette: ['#93c5fd', '#c4b5fd', '#e0f2fe', '#fde68a'], ambient: 'lights',
    title: 'Happy Maha Shivaratri!', emoji: '🔱', accent: '#4f46e5',
    message: 'May Lord Shiva bless you with strength, peace and wisdom. Har Har Mahadev!',
  },
  {
    id: 'holi', anchor: lunar('holi'), before: 2, after: 1, // Holika Dahan window → day after Dhulandi
    effect: 'colors', ambient: 'colors',
    title: 'Happy Holi!', emoji: '🎨', accent: '#ec4899',
    message: 'Wishing you a colourful Holi filled with laughter, love and happiness.',
  },
  {
    id: 'raksha-bandhan', anchor: lunar('raksha-bandhan'), before: 0, after: 0,
    effect: 'petals', palette: ['#f43f5e', '#fb7185', '#f59e0b', '#fbbf24', '#e11d48'], ambient: 'petals',
    title: 'Happy Raksha Bandhan!', emoji: '🪢', accent: '#e11d48',
    message: 'Celebrating the bond of love, care and protection between siblings.',
  },
  {
    id: 'janmashtami', anchor: lunar('janmashtami'), before: 0, after: 1,
    effect: 'petals', palette: ['#1d4ed8', '#0ea5e9', '#facc15', '#16a34a', '#7c3aed'], ambient: 'petals',
    title: 'Happy Janmashtami!', emoji: '🪈', accent: '#1d4ed8',
    message: 'May Lord Krishna fill your life with joy, love and wisdom. Jai Shri Krishna!',
  },
  {
    id: 'ganesh-chaturthi', anchor: lunar('ganesh-chaturthi'), before: 0, after: 10, // through Anant Chaturdashi
    effect: 'petals', palette: ['#f97316', '#fb923c', '#facc15', '#ef4444'], ambient: 'garland',
    title: 'Happy Ganesh Chaturthi!', emoji: '🐘', accent: '#ea580c',
    message: 'May Lord Ganesha remove all obstacles and bless you with success. Ganpati Bappa Morya!',
  },
  {
    id: 'navratri', anchor: lunar('navratri'), before: 0,
    end: (year) => { const d = lunarDate('dussehra', year); return d && addDays(d, -1); },
    effect: 'petals', palette: ['#dc2626', '#f97316', '#facc15', '#ec4899', '#16a34a'], ambient: 'garland',
    title: 'Happy Navratri & Durga Puja!', emoji: '🪔', accent: '#dc2626',
    message: 'May Maa Durga bless you with strength, courage and prosperity.',
  },
  {
    id: 'dussehra', anchor: lunar('dussehra'), before: 0, after: 0,
    effect: 'fireworks', ambient: 'sparks',
    title: 'Happy Dussehra!', emoji: '🏹', accent: '#d97706',
    message: 'May the victory of good over evil inspire you every day.',
  },
  {
    id: 'diwali', anchor: lunar('diwali'), before: 10, after: 2, // ~Dhanteras season → Bhai Dooj
    effect: 'fireworks', ambient: 'diwali',
    title: 'Happy Diwali!', emoji: '🪔', accent: '#f59e0b',
    message: 'May the festival of lights bring joy, prosperity and success to you and your family.',
  },
  {
    id: 'guru-nanak-jayanti', anchor: lunar('guru-nanak-jayanti'), before: 0, after: 0,
    effect: 'lights', palette: ['#fbbf24', '#f59e0b', '#fde68a', '#fb923c'], ambient: 'lights',
    title: 'Happy Gurpurab!', emoji: '🙏', accent: '#d97706',
    message: 'On Guru Nanak Jayanti, may the light of his teachings guide you always.',
  },
  {
    id: 'christmas', anchor: fixed(12, 25), before: 0, after: 0,
    effect: 'snow', ambient: 'snow',
    title: 'Merry Christmas!', emoji: '🎄', accent: '#16a34a',
    message: 'Wishing you peace, joy and warmth this holiday season.',
  },
  {
    id: 'new-year', anchor: fixed(12, 31), before: 0, after: 1, // Dec 31 → Jan 1
    effect: 'confetti', ambient: 'confetti',
    title: 'Happy New Year!', emoji: '🎉', accent: '#8b5cf6',
    message: 'Cheers to a new year of growth, success and great work together.',
  },
];

function lunarDate(id, year) {
  const mmdd = LUNAR_DATES[id]?.[year];
  if (!mmdd) return null;
  const [m, d] = mmdd.split('-').map(Number);
  return new Date(year, m - 1, d);
}

// Whole-day number in local time, so DST and time-of-day never shift a window.
function dayNumber(date) {
  return Math.round(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000);
}

function addDays(date, n) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
}

// Start/end of a festival's window for the given year, or null if unknown.
export function getFestivalWindow(festival, year) {
  const anchor = festival.anchor(year);
  if (!anchor) return null;
  const end = festival.end ? festival.end(year) : addDays(anchor, festival.after);
  if (!end) return null;
  return { anchor, start: addDays(anchor, -festival.before), end };
}

// Returns the active festival for `date` (default: today), or null.
// Checks the neighbouring years too, so windows crossing Dec 31 → Jan 1 work.
export function getActiveFestival(date = new Date()) {
  const today = dayNumber(date);
  const year = date.getFullYear();

  for (const festival of FESTIVALS) {
    for (const y of [year - 1, year, year + 1]) {
      const win = getFestivalWindow(festival, y);
      if (win && today >= dayNumber(win.start) && today <= dayNumber(win.end)) {
        return { ...festival, ...win };
      }
    }
  }
  return null;
}

export function getFestivalById(id, date = new Date()) {
  const festival = FESTIVALS.find((f) => f.id === id);
  if (!festival) return null;
  return { ...festival, ...getFestivalWindow(festival, date.getFullYear()) };
}

// Festival to use right now: a ?festival=<id> preview in the URL wins, else today's.
export function getCurrentFestival() {
  const previewId = new URLSearchParams(window.location.search).get('festival');
  return (previewId && getFestivalById(previewId)) || getActiveFestival();
}

// ---- shared state: login greeting + ambient theme ---------------------------
// Tiny pub-sub so the greeting (on dashboards) and the ambient theme (in layouts)
// can coordinate without a React context.

const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

export function subscribeFestival(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// "Just logged in" flag. Kept in memory only: set by a successful login, it
// survives the client-side redirect to the dashboard but is wiped by a page
// refresh, so refreshing never replays the greeting.
let loginGreetingPending = false;
let greetingOpen = false;

export function requestLoginGreeting() { loginGreetingPending = true; notify(); }
export function isLoginGreetingPending() { return loginGreetingPending; }
export function clearLoginGreeting() { loginGreetingPending = false; notify(); }

export function setGreetingOpen(open) { greetingOpen = open; notify(); }
export function isGreetingOpen() { return greetingOpen; }

// Ambient "festive vibe" on/off — a per-browser preference.
const VIBE_KEY = 'festive_vibe_off';

export function isFestiveVibeOn() {
  try { return localStorage.getItem(VIBE_KEY) !== '1'; } catch { return true; }
}

export function setFestiveVibeOn(on) {
  try { on ? localStorage.removeItem(VIBE_KEY) : localStorage.setItem(VIBE_KEY, '1'); } catch { /* ignore */ }
  notify();
}
